// --------------------------------------------------------------
// Created On: 2022-01-04
// Author: Zachary Thomas
//
// Last Modified: 2026-11-02
// Modified By: Zachary Thomas
//
// Copyright 2024 - 2026 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { useState } from "react";
import GetAssetAlertLogResponseBody from "src/types/api/GetAssetAlertLogResponseBody";
import getAssetDisplayName from "src/utilities/getAssetDisplayName";
import { API } from "../../constants/miscellaneous";
import useApi from "../../hooks/useApi";
import useReduxSelector from "../../hooks/useReduxSelector";
import { useParams } from "react-router-dom";
import ResourceList from "../../components/ResourceList/ResourceList";
import HelpAlertLog from "../../components/HelpAlertLog/HelpAlertLog";
import AlertLogListRow from "./AlertLogListRow/AlertLogListRow";
import AlertLogModal from "./AlertLogModal/AlertLogModal";
import Error500Page from "../Error500Page/Error500Page";
import Spinner from "../../components/Spinner/Spinner";
import { MANAGE_ALERT_THRESHOLDS_PERMISSION } from "src/constants/permissions";
import AssetThresholdsModal from "../ManageAlertThresholdsPage/AssetThresholdsModal/AssetThresholdsModal";

// Page for viewing alert logs for a specific asset.
export default function AssetAlertLogsPage(): Component {
  const [loading, setLoading] = useState<boolean>(false);
  const [assetName, setAssetName] = useState<string>("");
  const [assetNickname, setAssetNickname] = useState<string>("");
  const [isRental, setIsRental] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [showThresholdsModal, setShowThresholdsModal] = useState<boolean>(false);
  const [failedToLoad, setFailedToLoad] = useState<boolean>(false);
  const [alertLogs, setAlertLogs] = useState<AlertLog[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [userGroups, setUserGroups] = useState<UserGroup[]>([]);
  const [attributes, setAttributes] = useState<Attribute[]>([]);
  const [comparators, setComparators] = useState<Comparator[]>([]);
  const [enumerationMap, setEnumerationMap] = useState<EnumerationMap>({});
  const [companyOwnsAsset, setCompanyOwnsAsset] = useState<boolean>(false);
  const [selectedAlertLogId, setSelectedAlertLogId] = useState<number>(0);
  const assetDisplayName = getAssetDisplayName(assetName, assetNickname);
  const currentUser = useReduxSelector((state) => state.currentUser);
  const { assetId } = useParams();

  // Handle the selection of an alert log.
  function handleAlertLogSelection(alertLogId: number): void {
    setSelectedAlertLogId(alertLogId);
    setShowModal(true);
  }

  // Get alert log data from API.
  useApi(
    () => {
      if (assetId !== undefined) {
        setLoading(true);
        return true;
      } else {
        setLoading(false);
        return false;
      }
    },
    {
      method: "GET",
      url: `${API}/company/${currentUser.companyId}/asset/${assetId}/alertlog`,
    },
    async (response: Response, responseBody: GetAssetAlertLogResponseBody) => {
      if (response.ok && responseBody) {
        setAssetName(responseBody.assetName);
        if (responseBody.assetNickname !== undefined) {
          setAssetNickname(responseBody.assetNickname);
        }
        setIsRental(responseBody.isRental);
        setAlertLogs(responseBody.alertLogs);
        setUsers(responseBody.users);
        setUserGroups(responseBody.usergroups);
        setAttributes(responseBody.attributes);
        setComparators(responseBody.alertThresholdComparators);
        setEnumerationMap(responseBody.enumerationMap);
        setCompanyOwnsAsset(responseBody.companyOwnsAsset);
        setFailedToLoad(false);
      } else {
        setFailedToLoad(true);
      }
      setLoading(false);
    },
    [assetId]
  );

  return failedToLoad ? (
    <Error500Page />
  ) : (
    <div className="p-4">
      <Spinner loading={loading} />

      <ResourceList
        resourceNameSingular="Alert Log"
        resourceNamePlural={`${assetDisplayName}${assetName === "" ? "" : "'s"} Alert Logs`}
        resourceArticle="an"
        headerButtonLabel="Modify Alert Thresholds"
        headerButtonLabelSmall="Modify Thresholds"
        headerButtonUserPermissions={[[MANAGE_ALERT_THRESHOLDS_PERMISSION]]}
        resourceIdKey="alertLogId"
        resourcePriorityKey="createdUtc"
        resources={alertLogs}
        resourceRow={AlertLogListRow}
        helpModal={<HelpAlertLog />}
        loading={loading}
        showButton={companyOwnsAsset}
        onClickHeaderButton={() => setShowThresholdsModal(true)}
        onSelect={(alertLogId) => handleAlertLogSelection(alertLogId)}
      />

      <AlertLogModal showModal={showModal} alertLogId={selectedAlertLogId} onClose={() => setShowModal(false)} />

      <AssetThresholdsModal
        showModal={showThresholdsModal}
        assetId={assetId !== undefined ? parseInt(assetId) : 0}
        name={assetName}
        isRental={isRental || false}
        unassignedUsers={users}
        userGroups={userGroups}
        attributes={attributes}
        comparators={comparators}
        assets={[]}
        assetgroups={[]}
        enumerationMap={enumerationMap}
        onClose={() => setShowThresholdsModal(false)}
      />

      <datalist id="datalist-all-attributes">
        {attributes.map((attribute) => (
          <option key={attribute.regAttributeId} value={attribute.attributeName} />
        ))}
      </datalist>

      <datalist id="datalist-users">
        {users.map((user) => (
          <option key={user.userId} value={`${user.name} (${user.emailAddress})`} />
        ))}
      </datalist>

      <datalist id="datalist-user-groups">
        {userGroups.map((userGroup) => (
          <option key={userGroup.usergroupId} value={userGroup.name} />
        ))}
      </datalist>
    </div>
  );
}

interface AlertLog {
  alertLogId: number;
  assetName: string;
  message: string;
  isAcknowledged: boolean;
  isTimedOut: boolean;
  createdUtc: string;
}

interface User {
  userId: number;
  emailAddress: string;
  name: string;
  hasPhoneNumber: boolean;
}

interface UserGroup {
  usergroupId: number;
  name: string;
  userIds: number[];
}

interface Attribute {
  regAttributeId: number;
  attributeCode: string;
  attributeName: string;
  unitId: number | null;
  unitShortName: string | null;
  isBoolean: boolean;
  regEnumId: number | null;
}

interface Comparator {
  alertThresholdComparatorId: number;
  name: string;
}

interface EnumerationMap {
  [key: string]: EnumerationIndexMap;
}

interface EnumerationIndexMap {
  [key: string]: string;
}
